import React, { useEffect, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import {
  SegmentedControl,
  SegmentedItem,
} from '@/design-system/segmented-control';
import {
  BatchReassignMode,
  DispatchDetailBatch,
  DispatchRiderSnapshot,
} from '@/services/api/dispatches';

interface ReassignBatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The single batch being moved. Null = modal closed / not opened. */
  batch: DispatchDetailBatch | null;
  /** All riders on the dispatch; the batch's current owner is filtered out. */
  riders: DispatchRiderSnapshot[];
  onSubmit: (
    batchId: string,
    toRiderId: number,
    mode: BatchReassignMode
  ) => Promise<void>;
}

const MODES: Array<{ key: BatchReassignMode; label: string; hint: string }> = [
  {
    key: 'TRANSFER',
    label: 'Transfer',
    hint: "Move this batch to the selected rider. If they already have a batch of the same kind (dropoff with dropoff, pickup with pickup), the stops merge into it and the trip is re-routed — they keep one batch, not two. A different-kind batch moves over intact as its own trip.",
  },
  {
    key: 'SWAP',
    label: 'Swap',
    hint: "Move this batch to the selected rider, and move all of that rider's current batches back to this batch's rider.",
  },
];

const ReassignBatchModal: React.FC<ReassignBatchModalProps> = ({
  isOpen,
  onClose,
  batch,
  riders,
  onSubmit,
}) => {
  const [mode, setMode] = useState<BatchReassignMode>('TRANSFER');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMode('TRANSFER');
      setSelectedId(null);
    }
  }, [isOpen]);

  // The source rider is implicit — it's whoever currently owns the batch.
  // Exclude it from the picker (reassigning to it is a 400 SAME_RIDER).
  const currentRider = riders.find((r) => r.id === batch?.assignedRiderId);
  const targets = riders.filter((r) => r.id !== batch?.assignedRiderId);
  const isSwap = mode === 'SWAP';

  const handleSubmit = async () => {
    if (!batch || selectedId == null) return;
    setSubmitting(true);
    try {
      await onSubmit(batch.id, selectedId, mode);
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = !submitting && batch != null && selectedId != null;

  const subtitle = batch
    ? currentRider
      ? `${batch.code} · from ${currentRider.name}`
      : batch.code
    : undefined;

  return (
    <FormModal
      open={isOpen}
      onClose={onClose}
      icon={<i className="ri-arrow-left-right-line" />}
      iconTone="primary"
      title={isSwap ? 'Swap batch' : 'Reassign batch'}
      subtitle={subtitle}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      footer={
        <div className="flex items-center justify-end gap-2 w-full">
          <Button tone="neutral" style="stroke" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {submitting
              ? isSwap
                ? 'Swapping…'
                : 'Reassigning…'
              : isSwap
              ? 'Swap batch'
              : 'Reassign'}
          </Button>
        </div>
      }
    >
      {/* Mode toggle — Transfer (one-way) vs Swap (two-way). */}
      <div className="mb-4">
        <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          Mode
        </p>
        <SegmentedControl
          value={mode}
          onValueChange={(v: string) => {
            if (v) setMode(v as BatchReassignMode);
          }}
        >
          {MODES.map((m) => (
            <SegmentedItem key={m.key} value={m.key} disabled={submitting}>
              {m.label}
            </SegmentedItem>
          ))}
        </SegmentedControl>
        <p className="mt-2 text-[12px] text-text-sub-600 leading-relaxed">
          {MODES.find((m) => m.key === mode)?.hint}
        </p>
      </div>

      {/* Target rider picker. */}
      <p className="mb-2 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
        {isSwap ? 'Swap with' : 'Move to'}
      </p>
      <div className="max-h-64 overflow-y-auto rounded-lg border border-stroke-soft-200 divide-y divide-stroke-soft-200">
        {targets.length === 0 ? (
          <div className="px-3 py-4 text-center text-xs text-text-sub-600">
            No other riders in this dispatch's snapshot.
          </div>
        ) : (
          targets.map((r) => {
            const checked = selectedId === r.id;
            return (
              <label
                key={r.id}
                className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                  checked ? 'bg-bg-weak-50 border-l-2 border-l-accent' : 'border-l-2 border-l-transparent hover:bg-bg-weak-50'
                }`}
              >
                <input
                  type="radio"
                  name="reassign-batch-rider"
                  checked={checked}
                  onChange={() => setSelectedId(r.id)}
                  disabled={submitting}
                  className="h-4 w-4 border-stroke-soft-200 text-accent focus:ring-accent"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] text-text-strong-950 tracking-tight truncate">
                    {r.name}
                  </span>
                  <span className="block font-mono text-[11px] text-text-sub-600 tabular-nums">
                    {r.code}
                  </span>
                </span>
              </label>
            );
          })
        )}
      </div>
    </FormModal>
  );
};

export default ReassignBatchModal;
